import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import Navbar from "@/components/app/Navbar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Users, Home, MessageSquare, AlertTriangle } from "lucide-react";

export default function Admin() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  const load = async () => {
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get("/admin/reports"), api.get("/admin/users"), api.get("/admin/analytics")
      ]);
      setReports(r1.data.reports); setUsers(r2.data.users); setAnalytics(r3.data);
    } catch (e) { toast.error("Admin data failed"); }
  };
  useEffect(() => { load(); }, []);

  if (!user?.is_admin) return <><Navbar /><div className="max-w-3xl mx-auto p-8">Admin only.</div></>;

  const resolve = async (rid) => { await api.post(`/admin/reports/${rid}/resolve`); load(); toast.success("Resolved"); };
  const hide = async (lid) => { await api.post(`/admin/listings/${lid}/moderate?action=hide`); toast.success("Hidden"); };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display text-3xl md:text-4xl font-medium">Admin</h1>
        <p className="text-stone-600 mt-1">Moderation & city funnel</p>

        {analytics && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat icon={Users} label="Users" value={analytics.totals.users} />
            <Stat icon={Home} label="Listings" value={analytics.totals.listings} />
            <Stat icon={Home} label="Active" value={analytics.totals.active_listings} />
            <Stat icon={MessageSquare} label="Enquiries" value={analytics.totals.enquiries} />
            <Stat icon={AlertTriangle} label="Match rate" value={`${analytics.totals.match_rate}%`} />
          </div>
        )}

        <Tabs defaultValue="reports" className="mt-8">
          <TabsList>
            <TabsTrigger value="reports" data-testid="admin-tab-reports">Reports ({reports.length})</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Users ({users.length})</TabsTrigger>
            <TabsTrigger value="funnel" data-testid="admin-tab-funnel">City funnel</TabsTrigger>
          </TabsList>

          <TabsContent value="reports" className="space-y-2 mt-4">
            {reports.length === 0 && <Empty msg="No reports." />}
            {reports.map((r) => (
              <div key={r.id} className="p-4 bg-white border border-stone-200 rounded-2xl flex items-center gap-4">
                <div className="flex-1">
                  <div className="text-sm"><b>{r.target_type}</b> · {r.target_id}</div>
                  <div className="text-sm text-stone-600">Reason: {r.reason} · {r.notes}</div>
                  <div className="text-xs text-stone-400">{new Date(r.created_at).toLocaleString()}</div>
                </div>
                <span className="chip capitalize">{r.status}</span>
                {r.status === "open" && <>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => resolve(r.id)}>Resolve</Button>
                  {r.target_type === "listing" && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => hide(r.target_id)}>Hide</Button>}
                </>}
              </div>
            ))}
          </TabsContent>

          <TabsContent value="users" className="mt-4">
            <div className="overflow-x-auto bg-white border border-stone-200 rounded-2xl">
              <table className="w-full text-sm">
                <thead className="text-stone-500 text-left border-b border-stone-200">
                  <tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">City</th><th className="p-3">Plan</th><th className="p-3">Flags</th></tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-b border-stone-100">
                      <td className="p-3 font-medium">{u.name}</td>
                      <td className="p-3 text-stone-600">{u.email}</td>
                      <td className="p-3 capitalize">{u.role}</td>
                      <td className="p-3">{u.city}</td>
                      <td className="p-3 capitalize">{u.plan}</td>
                      <td className="p-3">{u.broker_flag && <span className="chip bg-red-50 text-red-700">Broker?</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="funnel" className="mt-4 grid md:grid-cols-2 gap-4">
            <FunnelCard title="Users by city" rows={analytics?.users_by_city || []} />
            <FunnelCard title="Listings by city" rows={analytics?.listings_by_city || []} />
            <FunnelCard title="Events" rows={analytics?.events || []} span />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
const Stat = ({ icon: Icon, label, value }) => (
  <div className="p-4 bg-white border border-stone-200 rounded-2xl">
    <Icon className="w-4 h-4 text-brand mb-2" />
    <div className="text-xs uppercase tracking-widest text-stone-500">{label}</div>
    <div className="font-display text-2xl font-semibold text-stone-900 mt-1">{value}</div>
  </div>
);
const Empty = ({ msg }) => <div className="text-center py-12 text-stone-500 border border-dashed border-stone-300 rounded-2xl">{msg}</div>;
const FunnelCard = ({ title, rows, span }) => (
  <div className={`p-5 bg-white border border-stone-200 rounded-2xl ${span ? "md:col-span-2" : ""}`}>
    <div className="font-display font-medium mb-3">{title}</div>
    {rows.length === 0 && <div className="text-sm text-stone-500">No data</div>}
    {rows.map((r) => (
      <div key={r._id || r.event} className="flex justify-between py-1 text-sm border-b border-stone-100 last:border-0">
        <span className="text-stone-700">{r._id || "—"}</span>
        <span className="text-stone-500">{r.count}</span>
      </div>
    ))}
  </div>
);
